import { type PageLayoutDetail, type PageLayoutData } from 'app/shop-ui/types';
import { type LAYOUT_SECTION_NAME } from 'shop-ui/constants';

function isHalfVisible(el: Element) {
    const rect = el.getBoundingClientRect();

    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    const visibleTop = Math.max(0, rect.top);

    const visibleBottom = Math.min(windowHeight, rect.bottom);

    const visibleHeight = visibleBottom - visibleTop;

    return visibleHeight >= rect.height / 2;
}

export function scrollToElement(elementId: string | null, passively: boolean = false) {
    if (!elementId) {
        return;
    }

    const sectionNode = document.getElementById(elementId);

    if (sectionNode) {
        if (passively) {
            if (!isHalfVisible(sectionNode)) {
                sectionNode.scrollIntoView();
            }

            return;
        }

        sectionNode.classList.add('focus-flash');

        sectionNode.scrollIntoView();

        const handleAnimationEnd = () => {
            sectionNode.classList.remove('focus-flash');

            sectionNode.removeEventListener('animationend', handleAnimationEnd);
        };

        sectionNode.addEventListener('animationend', handleAnimationEnd);
    }
}

export function transformToBasicClientLayout(data: Layout): PageLayoutDetail {
    const { id, layout, section, page } = data;

    const _layout = layout as PageLayoutData;

    return {
        layoutId: id,
        sectionName: section as LAYOUT_SECTION_NAME,
        pageId: page?.id ?? null,
        layout: _layout,
    };
}

export function getNativeDOMElement(
    elementId: string | null,
    query: string = 'data-shop-element-id'
) {
    if (!elementId || typeof window === 'undefined') {
        return null;
    }

    const dom = document.querySelector(`[${query}="${elementId}"]`);

    if (!dom) {
        return null;
    }

    return dom;
}

export function getHeaderHeight() {
    if (typeof window === 'undefined') {
        return '0px';
    }

    const header = document.querySelector('header');

    if (!header) {
        return '0px';
    }

    const computedStyle = getComputedStyle(header);

    return computedStyle.height;
}

export function getFooterHeight() {
    if (typeof window === 'undefined') {
        return '0px';
    }

    const footer = document.querySelector('footer');

    if (!footer) {
        return '0px';
    }

    const computedStyle = getComputedStyle(footer);

    return computedStyle.height;
}

export function getElementRectOffset(element: Element) {
    if (typeof window === 'undefined') {
        return {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
        };
    }

    const rect = element.getBoundingClientRect();

    return {
        x: window.scrollX + rect.left,
        y: window.scrollY + rect.top,
        width: rect.width,
        height: rect.height,
    };
}

export function extractGridCellSizeNumberValue(value?: string) {
    if (typeof value === 'undefined') {
        return 1;
    }

    if (/^\d+\.?(?:\d+)?fr$/.test(value)) {
        return Number(value.replace('fr', ''));
    }

    return 1;
}

export function getElementComputedStyleNumberValue(
    element: Element | null,
    property: string
) {
    if (!element || typeof window === 'undefined') {
        return 0;
    }

    let result = 0;

    const computedStyle = getComputedStyle(element);

    const styleValueStr = computedStyle[property].replace('px', '') as string;

    if (styleValueStr) {
        result = parseFloat(styleValueStr);

        if (Number.isNaN(result)) {
            result = 0;
        }
    }

    return result;
}

export function compareTwoCellSizes(cell1Size: number, cell2Size: number) {
    const cell1 = Math.round(cell1Size * 100) / 100;

    const cell2 = Math.round(cell2Size * 100) / 100;

    return cell1.toFixed(0) === cell2.toFixed(0);
}
